// AB#9994 — lead clause of the meeting kiosk's "Need help?" sheet.
//
// Kept in its own module with zero runtime imports so it can be unit-tested the
// same way pickerGrouping.ts is (transpiled in-memory by __tests__).

/**
 * Default lead clause of the kiosk "Need help?" sheet.
 *
 * Agency-neutral on purpose. The previous wording named "the front desk", which
 * does not exist in every venue — at the LA County 8/6/2026 dry run it sent
 * residents looking for a place that isn't there. "A member of our team" is true
 * in a chamber, a lobby, and a community room alike.
 *
 * Per-kiosk overrides live on KioskMode.HelpContent; this is only the fallback.
 */
export const DEFAULT_HELP_LEAD = "Please ask a member of our team";

/**
 * Resolve the lead clause, which is concatenated with a static
 * " for sign-in help, language assistance, or questions about the agenda items."
 *
 * Two things an admin will plausibly type would otherwise render badly on a
 * board-chamber screen, with no preview to catch it:
 *  - a complete sentence ("Ask the clerk at the dais.") → "…dais. for sign-in help"
 *  - whitespace only, which is truthy, so a bare `||` would render a bold blank
 *
 * Trailing sentence punctuation is stripped (ASCII plus the CJK/fullwidth forms an
 * admin writing Chinese, Japanese or Korean would type) so the clause always
 * composes. Empty after trimming falls back to the default.
 */
export function resolveHelpLead(raw: string | undefined | null): string {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return DEFAULT_HELP_LEAD;
  return (
    trimmed.replace(/[.。．!！?？;；,，:：]+$/u, "").trim() || DEFAULT_HELP_LEAD
  );
}
